import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getCurrentUser, signOut, signIn } from 'aws-amplify/auth';
import { refreshSession } from '../services/RefreshSession';
import AcademicApplicationService from '../services/AcademicApplicationService';
import { setupConsentListener } from '../utils/gaConsent';
import { UNCONFIRMED_USER_MESSAGE } from '../utils/resendConfirmationEmail';
import { enrichUserWithCognitoAttributes } from '../utils/enrichUserWithCognitoAttributes';
import { extractTrialFieldsFromUser, logTrialDebug } from '../utils/trialDebug';
import { mergeTrialAttributes } from '../utils/trialUtils';

export interface AuthUser {
  sub?: string;
  email?: string;
  username?: string;
  [key: string]: any;
}

export interface AuthState {
  // Core auth state
  isAuthenticating: boolean;
  authError: string;
  authorization: string;
  groups: string[];
  user: AuthUser | null;
  userApiKey: string[];
  userType: string | null;
  trialAttributes: {
    createdAt: string | null;
    extendedTrialDays: number | null;
    trialExtendedAt: string | null;
  } | null;
  trialAttributesLoaded: boolean;
  isAuthenticated: boolean;

  // Actions
  login: (credentials?: { username: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  setAuthorization: (token: string) => void;
  setGroups: (groups: string[]) => void;
  setUserApiKey: (apiKey: string[]) => void;
  setAuthError: (error: string) => void;
  setUserType: (userType: string | null) => void;
  setTrialAttributes: (
    trialAttributes: {
      createdAt: string | null;
      extendedTrialDays: number | null;
      trialExtendedAt: string | null;
    } | null,
  ) => void;
  setTrialAttributesLoaded: (loaded: boolean) => void;
  initializeAuth: () => Promise<void>;
  fetchUserType: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      // Initial state
      isAuthenticating: true,
      authError: '',
      authorization: '',
      groups: [],
      user: null,
      userApiKey: [],
      userType: null,
      trialAttributes: null,
      trialAttributesLoaded: false,
      isAuthenticated: false,

      // Actions
      setUser: user => set({ user, isAuthenticated: !!user }),
      setAuthorization: authorization => set({ authorization }),
      setGroups: groups => {
        set({ groups });
        // Notify restrictions store of group changes only if user exists
        const { user } = get();
        if (user) {
          const isAuthenticated = !!user;
          notifyRestrictionsUpdate(isAuthenticated, groups);
        }
      },
      setUserApiKey: userApiKey => set({ userApiKey }),
      setAuthError: authError => set({ authError }),
      setUserType: userType => set({ userType }),
      setTrialAttributes: trialAttributes => set({ trialAttributes }),
      setTrialAttributesLoaded: trialAttributesLoaded => set({ trialAttributesLoaded }),

      login: async (credentials?: { username: string; password: string }) => {
        set({ isAuthenticating: true, authError: '' });
        try {
          // If credentials are provided, perform sign in first
          if (credentials) {
            // Check if there's already a signed in user and sign them out first
            try {
              const currentUser = await getCurrentUser();
              if (currentUser) {
                await signOut();
              }
            } catch (error) {
              // No current user, proceed with login
            }

            // Perform the sign in
            const signInResult = await signIn({
              username: credentials.username,
              password: credentials.password,
            });

            if (!signInResult.isSignedIn) {
              if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
                throw new Error(UNCONFIRMED_USER_MESSAGE);
              } else {
                throw new Error('An error occurred. Please contact it@disgenet.com');
              }
            }
          }

          // Get user and session info
          const user = await getCurrentUser();
          const session = await refreshSession();
          const userInfo = session.tokens.idToken.payload;
          const userGroups = (userInfo['cognito:groups'] as string[]) || [];

          const userWithAttributes = await enrichUserWithCognitoAttributes({
            ...userInfo,
            ...user,
          });

          logTrialDebug('authStore.login user attributes', {
            jwtPayload: extractTrialFieldsFromUser(userInfo as Record<string, unknown>),
            mergedUser: extractTrialFieldsFromUser(userWithAttributes),
          });

          set({
            user: userWithAttributes,
            authorization: session.tokens.idToken.toString(),
            groups: userGroups,
            isAuthenticating: false,
            isAuthenticated: true,
          });

          // Notify restrictions store of successful login
          notifyRestrictionsUpdate(true, userGroups);

          // Setup consent listener if user has sub
          if (userInfo?.sub) {
            setupConsentListener(userInfo.sub);
          }

          // Fetch user type
          get().fetchUserType();

          // Show Limited Academic Access modal only once per login (not on page reload)
          if (userGroups.includes('ROLE_ACADEMIC')) {
            sessionStorage.setItem('showAcademicUpgradeModal', '1');
          }

          setTimeout(() => {
            const currentState = get();
            set({ ...currentState });
          }, 50);
        } catch (error) {
          console.error('🔐 Login error:', error);

          let errorMessage = 'Login failed';
          if (error instanceof Error) {
            if (error.message.includes('User is not in any known userGroup')) {
              errorMessage =
                'Your account does not have the required permissions. Please contact it@disgenet.com';
            } else if (error.message.includes('Error retrieving user')) {
              errorMessage = 'Unable to retrieve user information. Please contact it@disgenet.com';
            } else if (error.message.includes('confirmation link')) {
              errorMessage = UNCONFIRMED_USER_MESSAGE;
            } else {
              errorMessage = error.message;
            }
          }

          set({
            authError: errorMessage,
            isAuthenticating: false,
            isAuthenticated: false,
          });
          await get().logout();
          throw new Error(errorMessage);
        }
      },

      logout: async () => {
        try {
          await signOut();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            isAuthenticating: false,
            authorization: '',
            groups: [],
            user: null,
            userApiKey: [],
            userType: null,
            trialAttributes: null,
            trialAttributesLoaded: false,
            authError: '',
            isAuthenticated: false,
          });

          // Notify restrictions store of logout
          notifyRestrictionsUpdate(false, []);
        }
      },

      initializeAuth: async () => {
        set({ isAuthenticating: true });
        try {
          // Check if user is signed in (throws error if not authenticated)
          const user = await getCurrentUser();
          const session = await refreshSession();
          const userInfo = session.tokens.idToken.payload;
          const userGroups = (userInfo['cognito:groups'] as string[]) || [];

          const userWithAttributes = await enrichUserWithCognitoAttributes({
            ...userInfo,
            ...user,
          });

          logTrialDebug('authStore.initializeAuth user attributes', {
            jwtPayload: extractTrialFieldsFromUser(userInfo as Record<string, unknown>),
            mergedUser: extractTrialFieldsFromUser(userWithAttributes),
          });

          set({
            user: userWithAttributes,
            authorization: session.tokens.idToken.toString(),
            groups: userGroups,
            isAuthenticated: true,
          });

          // Notify restrictions store of successful initialization
          notifyRestrictionsUpdate(true, userGroups);

          // Setup consent listener if user has sub
          if (userInfo?.sub) {
            setupConsentListener(userInfo.sub);
          }

          // Fetch user type
          get().fetchUserType();
        } catch (e) {
          // Not logged in user - cleanup session
          set({
            groups: [],
            authorization: '',
            user: null,
            userType: null,
            trialAttributes: null,
            trialAttributesLoaded: false,
            isAuthenticated: false,
          });

          // Notify restrictions store for unauthenticated user
          notifyRestrictionsUpdate(false, []);

          // Only sign out if there was actually a session to clean up
          try {
            await signOut();
          } catch (signOutError) {
            // Ignore sign out errors when there's no session
          }
        } finally {
          set({ isAuthenticating: false });
        }
      },

      fetchUserType: async () => {
        const { user } = get();
        const isAuthenticated = !!user;
        if (isAuthenticated && user?.email) {
          set({ trialAttributesLoaded: false });
          try {
            const statusResponse = await AcademicApplicationService.getAcademicStatus(user.email);

            logTrialDebug('authStore.fetchUserType /academic-status response', {
              email: user.email,
              trialType: statusResponse.trialType ?? null,
              userType: statusResponse.userType ?? null,
              createdAt: statusResponse.createdAt ?? null,
              extendedTrialDays: statusResponse.extendedTrialDays ?? null,
              trialExtendedAt: statusResponse.trialExtendedAt ?? null,
              groups: statusResponse.groups ?? [],
            });

            // For trial users, use trialType; for others, use userType
            const type = statusResponse.trialType || statusResponse.userType || null;
            const nextTrialAttributes = {
              createdAt: statusResponse.createdAt ?? null,
              extendedTrialDays:
                statusResponse.extendedTrialDays != null ? statusResponse.extendedTrialDays : null,
              trialExtendedAt: statusResponse.trialExtendedAt ?? null,
            };
            const mergedUser = mergeTrialAttributes(user, nextTrialAttributes);

            logTrialDebug('authStore.fetchUserType merged user', {
              before: extractTrialFieldsFromUser(user),
              after: extractTrialFieldsFromUser(mergedUser),
            });

            set({
              user: mergedUser,
              userType: type,
              trialAttributes: nextTrialAttributes,
              trialAttributesLoaded: true,
            });
          } catch (error) {
            console.error('Error fetching user type:', error);
            logTrialDebug('authStore.fetchUserType failed', {
              email: user.email,
              error: error instanceof Error ? error.message : error,
            });
            set({ trialAttributesLoaded: true });
          }
        } else {
          set({ userType: null, trialAttributes: null, trialAttributesLoaded: false });
        }
      },
    }),
    {
      name: 'auth-store',
    },
  ),
);

// Function to notify restrictions store of auth changes
// This will be set by the restrictions store to avoid circular dependencies
let notifyRestrictionsUpdate: (isAuthenticated: boolean, groups: string[]) => void = () => {};

// Function to register the restrictions update callback
export const registerRestrictionsUpdater = (
  updater: (isAuthenticated: boolean, groups: string[]) => void,
) => {
  notifyRestrictionsUpdate = updater;
};

// Computed selectors
export const useAuthSelectors = () => {
  const { user, groups, userType } = useAuthStore();
  const isAuthenticated = !!user;

  return {
    isAuthenticated,
    currentUser: isAuthenticated
      ? {
          isLogged: true,
          role: groups?.[0] || 'user',
          userType: userType,
        }
      : null,
    isAcademic: groups.includes('ROLE_ACADEMIC'),
    isTrial: groups.includes('ROLE_TRIAL'),
    isPremium: !groups.includes('ROLE_ACADEMIC') && !groups.includes('ROLE_TRIAL') && isAuthenticated,
  };
};

// Selector for isAuthenticated
export const useIsAuthenticated = () => {
  return useAuthStore(state => !!state.user);
};
